import style from './style.module.scss';
import { useField } from 'formik';
import { useValidationFeedback } from '../../components/ValidationFeedbackFormik';
import { joinNames } from '../helpers';
import { ErrorMessage } from '../Container';
import NotificationIcon from '../NotificationIcon';
import Label from '../Label';
import { useHelpBox } from '../HelpBox';
import type { BaseFieldProps, UiStateProps } from '../Input';
import useDefaultId from '@n2-common/lib/useDefaultId';

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> &
    BaseFieldProps & {
        options: OptionProps[];
    };

export type OptionProps<T extends (string | number | undefined) = string> = {
    value: T;
    text: string;
};

export default function Select({
    name,
    options,
    label,
    error,
    id,
    uistate,
    className: outerClassName,
    helptext,
    ...attr
}: SelectProps & UiStateProps) {
    const compId = useDefaultId(id);
    const errorId = `${compId}error`;
    const [HelpBox, HelpIcon] = useHelpBox(helptext, name);

    return (
        <div className={joinNames(style.root, outerClassName)}>
            <div className={style.labelWrapper}>
                <Label htmlFor={compId}>{label}</Label>
                <HelpIcon />
            </div>
            <HelpBox />
            <div className={joinNames(style.wrapper, uistate === 'value-verified' && style.valueVerified, error ? style.error : undefined)}>
                <select name={name} id={compId} {...attr} aria-errormessage={errorId} aria-invalid={error ? true : undefined}>
                    {options.map(({ value: id, text }) => (
                        <option key={id} value={id}>
                            {text}
                        </option>
                    ))}
                </select>
                <NotificationIcon prefixForUiTesting={name} check={uistate === 'value-verified'} alert={!!error && { title: error }} />
            </div>
            {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        </div>
    );
}

export function FormikSelect({ name, options, label, ...attr }: SelectProps): JSX.Element {
    const [{ onChange, onBlur, value }, { error, touched }] = useField(name);
    const [verified, handleInput] = useValidationFeedback(name);
    return (
        <Select
            name={name}
            defaultValue={value}
            options={options}
            label={label}
            uistate={verified ? 'value-verified' : 'standard'}
            onInput={handleInput}
            onChange={onChange}
            onBlur={onBlur}
            error={touched && error}
            {...attr}
        />
    );
}
